import { motion } from "framer-motion";
import {
  BarChart3,
  Target,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
} from "recharts";

const performanceData = [
  { name: "Mon", revenue: 4200, users: 2400 },
  { name: "Tue", revenue: 5100, users: 3200 },
  { name: "Wed", revenue: 4800, users: 2800 },
  { name: "Thu", revenue: 6200, users: 4100 },
  { name: "Fri", revenue: 5800, users: 3900 },
  { name: "Sat", revenue: 7500, users: 5200 },
  { name: "Sun", revenue: 7100, users: 4800 },
];

const Performance = () => {
  return (
    <div className="space-y-8 animate-fadeIn pb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#111c44] p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
            System Performance
          </h1>
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mt-1">
            Advanced telemetry and operational efficiency metrics.
          </p>
        </div>
        <div className="flex gap-3">
          <button className="h-10 px-5 bg-white dark:bg-[#1b254b] border border-slate-200 dark:border-white/10 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-[#111c44] transition-all shadow-sm">
            Export Dataset
          </button>
          <button className="h-10 px-5 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-xl text-sm font-bold transition-all shadow-sm">
            Run Audit
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            title: "Response Time",
            value: "142ms",
            trend: "-12%",
            sub: "Global Average",
            icon: Zap,
            colorClass:
              "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400",
          },
          {
            title: "Throughput",
            value: "4.2k rps",
            trend: "+8.4%",
            sub: "Request Volume",
            icon: BarChart3,
            colorClass:
              "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400",
          },
          {
            title: "Efficiency",
            value: "98.2%",
            trend: "+0.5%",
            sub: "Resource Usage",
            icon: Target,
            colorClass:
              "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400",
          },
        ].map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="p-6 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm relative group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${stat.colorClass}`}>
                <stat.icon size={22} />
              </div>
              <div
                className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-md ${stat.trend.startsWith("+") ? "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400" : "bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400"}`}
              >
                {stat.trend.startsWith("+") ? (
                  <ArrowUpRight size={14} />
                ) : (
                  <ArrowDownRight size={14} />
                )}
                {stat.trend}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-500 mb-1">
                {stat.title}
              </p>
              <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
                {stat.value}
              </h3>
              <p className="text-xs text-slate-400 dark:text-slate-600 font-medium mt-1">
                {stat.sub}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="p-6 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm"
        >
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-6">
            System Reliability
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="colorPerf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="var(--chart-grid)"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 12,
                    fontWeight: 500,
                    fill: "var(--chart-text)",
                  }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 12,
                    fontWeight: 500,
                    fill: "var(--chart-text)",
                  }}
                  dx={-10}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid var(--chart-border)",
                    backgroundColor: "var(--chart-bg)",
                    color: "var(--chart-text)",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                  itemStyle={{ color: "var(--chart-text)" }}
                  labelStyle={{
                    color: "var(--chart-text)",
                    fontWeight: "bold",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#2563eb"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorPerf)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="p-6 bg-white dark:bg-[#111c44] rounded-2xl border border-slate-200 dark:border-white/10 shadow-sm"
        >
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-6">
            Node Deployment Density
          </h3>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={performanceData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="var(--chart-grid)"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fontWeight: 500, fill: "#64748b" }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 12,
                    fontWeight: 500,
                    fill: "var(--chart-text)",
                  }}
                  dx={-10}
                />
                <Tooltip
                  cursor={{ fill: "var(--chart-cursor)" }}
                  contentStyle={{
                    borderRadius: "12px",
                    border: "1px solid var(--chart-border)",
                    backgroundColor: "var(--chart-bg)",
                    color: "var(--chart-text)",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                  itemStyle={{ color: "var(--chart-text)" }}
                  labelStyle={{
                    color: "var(--chart-text)",
                    fontWeight: "bold",
                  }}
                />
                <Bar
                  dataKey="users"
                  fill="#6366f1"
                  radius={[6, 6, 0, 0]}
                  maxBarSize={40}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Performance;
